import { ChevronDown, Clock, DoorClosed, DoorOpen, Wallet } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useNavigate } from "react-router";
import { useConfirm } from "../../components/ConfirmDialog";
import { ApiError, providerApi, type ProviderVenue } from "../../lib/api";
import { useAppPreferences } from "../../lib/preferences";
import { CLOSE_EARLY_NOTE, getVenueStatus } from "../provider-availability/availability";
import { formatEgp } from "../provider-orders/order-status";
import { useProviderWorkspace } from "../provider-workspace/ProviderWorkspaceContext";
import { ShiftDialog, formatShiftTime } from "./ShiftDialog";
import { useCashShift } from "./useCashShift";

function useMinuteClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  return now;
}

/**
 * Open or closed for bookings, as one line. Tapping it explains where that comes
 * from and, for owners, offers Close early today / Reopen now.
 */
export function VenueStatusPill({ venue, canManage }: { venue: ProviderVenue; canManage: boolean }) {
  const { t } = useAppPreferences();
  const navigate = useNavigate();
  const { reload } = useProviderWorkspace();
  const { confirm, dialog } = useConfirm();
  const panelId = useId();
  const now = useMinuteClock();
  const status = getVenueStatus(venue, t, now);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const Icon = status.isOpenNow ? DoorOpen : DoorClosed;

  const run = async (action: () => Promise<unknown>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await reload(venue.id);
    } catch (requestError) {
      setError(requestError instanceof ApiError && requestError.status !== 0 ? requestError.detail : fallback);
    } finally {
      setBusy(false);
    }
  };

  const closeEarly = async () => {
    const window = status.closeEarlyWindow;
    if (!window) return;
    const ok = await confirm({
      title: t("Close early today?"),
      description: t("Players won't be able to book until {time}. Bookings already made for later today stay booked; check Bookings if you need to call anyone.", { time: window.end_local.slice(11) }),
      confirmButtonLabel: t("Close early"),
      cancelButtonLabel: t("Stay open"),
    });
    if (!ok) return;
    await run(() => providerApi.createVenueBlackoutWindow(venue.id, { ...window, note: CLOSE_EARLY_NOTE }), t("Could not close early. Try again."));
  };

  const reopen = async () => {
    const closure = status.activeClosure;
    if (!closure) return;
    await run(() => providerApi.deleteVenueBlackoutWindow(venue.id, closure.id), t("Could not reopen. Try again."));
  };

  return (
    <>
      {dialog}
      <button
        aria-controls={panelId}
        aria-expanded={expanded}
        className="home-pill"
        data-tone={status.isOpenNow ? "success" : "warning"}
        data-testid="venue-status-pill"
        onClick={() => setExpanded((value) => !value)}
        type="button"
      >
        <Icon aria-hidden="true" size={16} />
        <span className="home-pill-text">
          <span className="home-pill-title" id="venue-status-title">{status.isOpenNow ? t("Open for bookings") : t("Closed for bookings")}</span>
          <span className="home-pill-sub">{status.reason}</span>
        </span>
        <ChevronDown aria-hidden="true" className="home-pill-chevron" size={16} />
      </button>
      {expanded ? (
        <div className="home-pill-panel" id={panelId}>
          <p className="ops-copy text-sm">
            {status.source === "closure"
              ? status.closedEarly
                ? t("You closed early today. It reopens by itself at the next opening time.")
                : t("A planned closure is on. Manage closures in Venue setup → Hours.")
              : t("This follows your weekly opening hours. You don't need to open or close anything each day.")}
          </p>
          {error ? <div className="ops-banner" data-tone="danger" role="alert">{error}</div> : null}
          <div className="flex flex-wrap gap-2">
            {canManage && status.closeEarlyWindow ? (
              <button className="ops-button" data-size="sm" data-variant="secondary" disabled={busy} onClick={() => void closeEarly()} type="button">
                {t("Close early today")}
              </button>
            ) : null}
            {canManage && status.closedEarly ? (
              <button className="ops-button" data-size="sm" data-variant="primary" disabled={busy} onClick={() => void reopen()} type="button">
                {t("Reopen now")}
              </button>
            ) : null}
            <button className="ops-button" data-size="sm" data-variant="secondary" onClick={() => navigate("/provider/venue-setup?tab=hours")} type="button">
              <Clock aria-hidden="true" size={14} /> {t("Opening hours")}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}

/** The cash till in one line, with the one action that makes sense right now. */
export function ShiftPill({ venue, isOnline }: { venue: ProviderVenue; isOnline: boolean }) {
  const { t, language } = useAppPreferences();
  const shift = useCashShift(venue.id);
  const [dialogOpen, setDialogOpen] = useState(false);
  const open = shift.openShift;

  return (
    <>
      <div className="home-pill" data-tone={open ? "success" : undefined} data-testid="shift-pill">
        <Wallet aria-hidden="true" size={16} />
        <span className="home-pill-text">
          <span className="home-pill-title" id="shift-title">{open ? t("Shift open") : t("No shift open")}</span>
          <span className="home-pill-sub">
            {open
              ? t("{amount} in the till · since {time}", { amount: formatEgp(shift.expectedCash, language), time: formatShiftTime(open.opened_at, language) })
              : t("Tracks the cash in the till")}
          </span>
        </span>
        <button
          className="ops-button"
          data-size="sm"
          data-variant={open ? "secondary" : "primary"}
          disabled={!shift.summary}
          onClick={() => setDialogOpen(true)}
          type="button"
        >
          {open ? t("End shift") : t("Start shift")}
        </button>
      </div>
      <ShiftDialog isOnline={isOnline} onClose={() => setDialogOpen(false)} open={dialogOpen} venue={venue} />
    </>
  );
}

/**
 * Shown next to a cash payment when no shift is open, so the cash still gets
 * counted. It never blocks the payment.
 */
export function NoShiftCashNotice({ venue, isOnline }: { venue: ProviderVenue | null; isOnline: boolean }) {
  const { t } = useAppPreferences();
  const shift = useCashShift(venue?.id ?? null);
  const [dialogOpen, setDialogOpen] = useState(false);
  if (!venue || !shift.summary || shift.openShift) return null;
  return (
    <div className="ops-banner" data-tone="warning" role="status" data-testid="no-shift-notice">
      <span className="min-w-0 flex-1">{t("No shift is open, so this cash won't be counted in a till check.")}</span>
      <button className="ops-button" data-size="sm" data-variant="secondary" onClick={() => setDialogOpen(true)} type="button">
        {t("Start shift")}
      </button>
      <ShiftDialog isOnline={isOnline} onClose={() => setDialogOpen(false)} open={dialogOpen} venue={venue} />
    </div>
  );
}
